import { Button, Select } from '@artosera/ui';
import { TYPES_DOCUMENT, type Confidentialite, type TypeDocument } from '@artosera/shared';
import { Archive, Link2, Search, X } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FiltresDocuments } from '../../api/coffre.js';
import { BasculeFiltres, filtresRepliables } from '../filtres/BasculeFiltres.js';
import { RechercheRattachement } from './RechercheRattachement.js';
import { useRattachementResolu, type RattachementLu } from './useRattachementResolu.js';
import styles from './BarreCoffre.module.css';

export type PorteeEcheance = '' | 'enRetard' | 'sousTrente';
export type TriCoffre = NonNullable<FiltresDocuments['tri']>;

/** Tout ce que la barre pilote : un seul objet, une seule remise à zéro. */
export interface EtatBarreCoffre {
  q: string;
  type: TypeDocument | null;
  rattachement: RattachementLu | null;
  confidentialite: Confidentialite | '';
  porteeEcheance: PorteeEcheance;
  etiquette: string;
  inclureArchives: boolean;
  tri: TriCoffre;
}

export const ETAT_BARRE_VIDE: EtatBarreCoffre = {
  q: '',
  type: null,
  rattachement: null,
  confidentialite: '',
  porteeEcheance: '',
  etiquette: '',
  inclureArchives: false,
  tri: 'recent',
};

/** Filtres posés (hors recherche libre et tri) : ce que la barre rend effaçable. */
export function compterFiltresActifs(etat: EtatBarreCoffre): number {
  return [
    etat.type,
    etat.rattachement,
    etat.confidentialite,
    etat.porteeEcheance,
    etat.etiquette,
    etat.inclureArchives ? 'oui' : '',
  ].filter(Boolean).length;
}

/** Vrai dès que la liste est restreinte, recherche libre comprise. */
export function aUneRestriction(etat: EtatBarreCoffre): boolean {
  return compterFiltresActifs(etat) > 0 || etat.q.trim() !== '';
}

export interface BarreCoffreProps {
  galerieSlug: string;
  etat: EtatBarreCoffre;
  onChanger: (partiel: Partial<EtatBarreCoffre>) => void;
  onToutEffacer: () => void;
  /** Documents vivants par type (agrégat du tableau de bord, § 20). */
  comptesParType: Readonly<Record<string, number>>;
}

const TRIS: readonly TriCoffre[] = ['recent', 'ancien', 'titre', 'echeance'];

/**
 * La barre unique du coffre : une recherche, un tri, puis les critères en
 * puces (type, rattachement, confidentialité, échéance, étiquette, archives).
 * Ce qui est posé se relit dans la rangée « filtres actifs », où chaque
 * critère s'efface d'un clic. Les sélecteurs ne répètent pas leur valeur : la
 * rangée active en est l'unique miroir.
 */
export function BarreCoffre({
  galerieSlug,
  etat,
  onChanger,
  onToutEffacer,
  comptesParType,
}: BarreCoffreProps): JSX.Element {
  const { t } = useTranslation(['regie', 'coffre']);
  const [replie, setReplie] = useState(false);
  const [rattachementOuvert, setRattachementOuvert] = useState(false);
  const actifs = compterFiltresActifs(etat);

  return (
    <div className={styles.enveloppe}>
      <div
        className={`${styles.barre} ${filtresRepliables.racine}`}
        data-ouvert={replie}
        role="search"
        aria-label={t('regie:coffre.liste.filtresTitre')}
      >
        <div className={styles.champRecherche}>
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:coffre.liste.rechercheChamp')}
            aria-label={t('regie:coffre.liste.rechercheChamp')}
            value={etat.q}
            onChange={(event) => onChanger({ q: event.target.value })}
          />
        </div>

        <BasculeFiltres
          titre={t('regie:coffre.liste.filtresTitre')}
          actifs={actifs}
          ouvert={replie}
          onBasculer={() => setReplie((valeur) => !valeur)}
        />

        <div className={styles.tri}>
          <Select
            aria-label={t('regie:coffre.liste.tri.libelle')}
            value={etat.tri}
            onValueChange={(valeur) => onChanger({ tri: valeur as TriCoffre })}
            options={TRIS.map((code) => ({
              value: code,
              label: t(`regie:coffre.liste.tri.${code}`),
            }))}
          />
        </div>

        <div className={`${styles.puces} ${filtresRepliables.repliable}`}>
          <div className={styles.puce} data-actif={etat.type !== null}>
            <Select
              aria-label={t('regie:coffre.liste.filtres.type')}
              value=""
              placeholder={t('regie:coffre.liste.filtres.typeCourt')}
              onValueChange={(valeur) => onChanger({ type: valeur as TypeDocument })}
              options={TYPES_DOCUMENT.map((code) => ({
                value: code,
                label: `${t(`coffre:types.${code}`)} (${comptesParType[code] ?? 0})`,
              }))}
            />
          </div>

          <button
            type="button"
            className={styles.puceBouton}
            data-actif={etat.rattachement !== null}
            aria-expanded={rattachementOuvert}
            onClick={() => setRattachementOuvert((valeur) => !valeur)}
          >
            <Link2 size={14} strokeWidth={1.5} aria-hidden />
            {t('regie:coffre.liste.filtres.rattachement')}
          </button>

          <div className={styles.puce} data-actif={etat.confidentialite !== ''}>
            <Select
              aria-label={t('regie:coffre.liste.filtres.confidentialite')}
              value=""
              placeholder={t('regie:coffre.liste.filtres.confidentialite')}
              onValueChange={(valeur) => onChanger({ confidentialite: valeur as Confidentialite })}
              options={[
                { value: 'equipe', label: t('coffre:confidentialite.equipe') },
                { value: 'confidentiel', label: t('coffre:confidentialite.confidentiel') },
              ]}
            />
          </div>

          <div className={styles.puce} data-actif={etat.porteeEcheance !== ''}>
            <Select
              aria-label={t('regie:coffre.liste.filtres.echeance')}
              value=""
              placeholder={t('regie:coffre.liste.filtres.echeance')}
              onValueChange={(valeur) => onChanger({ porteeEcheance: valeur as PorteeEcheance })}
              options={[
                { value: 'sousTrente', label: t('regie:coffre.liste.filtres.sousTrenteJours') },
                { value: 'enRetard', label: t('regie:coffre.liste.filtres.enRetard') },
              ]}
            />
          </div>

          <input
            type="text"
            className={styles.saisieEtiquette}
            data-actif={etat.etiquette !== ''}
            placeholder={t('regie:coffre.liste.filtres.etiquette')}
            aria-label={t('regie:coffre.liste.filtres.etiquette')}
            value={etat.etiquette}
            onChange={(event) => onChanger({ etiquette: event.target.value })}
          />

          <button
            type="button"
            className={styles.puceBouton}
            data-actif={etat.inclureArchives}
            aria-pressed={etat.inclureArchives}
            onClick={() => onChanger({ inclureArchives: !etat.inclureArchives })}
          >
            <Archive size={14} strokeWidth={1.5} aria-hidden />
            {t('regie:coffre.liste.filtres.archives')}
          </button>
        </div>

        {rattachementOuvert ? (
          <div className={styles.panneauRattachement}>
            <RechercheRattachement
              galerieSlug={galerieSlug}
              valeurs={[]}
              onChanger={(valeurs) => {
                const choisi = valeurs[valeurs.length - 1];
                if (!choisi) return;
                onChanger({ rattachement: choisi });
                setRattachementOuvert(false);
              }}
            />
          </div>
        ) : null}
      </div>

      {actifs > 0 ? (
        <div
          className={styles.actifs}
          role="group"
          aria-label={t('regie:coffre.liste.filtres.actifs')}
        >
          {etat.type ? (
            <PuceActive
              libelle={t(`coffre:types.${etat.type}`)}
              retirer={() => onChanger({ type: null })}
            />
          ) : null}
          {etat.rattachement ? (
            <PuceRattachement
              galerieSlug={galerieSlug}
              rattachement={etat.rattachement}
              retirer={() => onChanger({ rattachement: null })}
            />
          ) : null}
          {etat.confidentialite ? (
            <PuceActive
              libelle={t(`coffre:confidentialite.${etat.confidentialite}`)}
              retirer={() => onChanger({ confidentialite: '' })}
            />
          ) : null}
          {etat.porteeEcheance ? (
            <PuceActive
              libelle={t(
                etat.porteeEcheance === 'enRetard'
                  ? 'regie:coffre.liste.filtres.enRetard'
                  : 'regie:coffre.liste.filtres.sousTrenteJours',
              )}
              retirer={() => onChanger({ porteeEcheance: '' })}
            />
          ) : null}
          {etat.etiquette ? (
            <PuceActive
              libelle={t('regie:coffre.liste.filtres.etiquetteValeur', {
                etiquette: etat.etiquette,
              })}
              retirer={() => onChanger({ etiquette: '' })}
            />
          ) : null}
          {etat.inclureArchives ? (
            <PuceActive
              libelle={t('regie:coffre.liste.filtres.archives')}
              retirer={() => onChanger({ inclureArchives: false })}
            />
          ) : null}
          <Button variant="ghost" size="sm" onClick={onToutEffacer}>
            {t('regie:coffre.liste.filtres.toutEffacer')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function PuceActive({ libelle, retirer }: { libelle: string; retirer: () => void }): JSX.Element {
  const { t } = useTranslation(['regie']);
  return (
    <button
      type="button"
      className={styles.puceActive}
      aria-label={t('regie:coffre.liste.filtres.retirer', { filtre: libelle })}
      onClick={retirer}
    >
      <span>{libelle}</span>
      <X size={12} strokeWidth={2} aria-hidden />
    </button>
  );
}

/** Le rattachement se lit par son nom (œuvre, artiste…), résolu comme sur la pastille. */
function PuceRattachement({
  galerieSlug,
  rattachement,
  retirer,
}: {
  galerieSlug: string;
  rattachement: RattachementLu;
  retirer: () => void;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const resolu = useRattachementResolu(galerieSlug, rattachement);
  const libelle = resolu.chargement
    ? t('commun:chargement')
    : (resolu.label ?? t('regie:coffre.rattachements.introuvable'));
  return <PuceActive libelle={libelle} retirer={retirer} />;
}
